// Section15.jsx — Mobile App Development Technologies
import React from 'react';
import { useReveal } from '../../componets/useScrollFX';
const techBanner = '../assets/Mobile App Development Technologies.webp';
const apple_logo = '../assets/apple.png';
const android_logo = '../assets/icons8-android-os.svg';
const reactnative_logo = '../assets/icons8-react-native.svg';
const nodejs_logo = '../assets/icons8-nodejs.svg';
const cloud_icon = '../assets/icons8-firebase.svg';
const database_icon = '../assets/icons8-postgresql-100.png';
const integration_icon = '../assets/icons8-card-payment-100.png';

const mobileStacks = [
  { icon: android_logo, iconType: "image", title: "Android Development", intro: null, items: ["Kotlin", "Java", "Android SDK"] },
  { icon: apple_logo, iconType: "image", title: "iOS Development", intro: null, items: ["Swift", "SwiftUI", "Objective-C"] },
  { icon: reactnative_logo, iconType: "image", title: "Cross-Platform Development", intro: null, items: ["Flutter", "React Native"] },
  { icon: nodejs_logo, iconType: "image", title: "Backend Development", intro: "We build reliable backend systems that support seamless mobile experiences.", items: ["Node.js", "PHP", "Python", ".NET", "REST APIs", "GraphQL"] },
];

const infraStacks = [
  { icon: cloud_icon, iconType: "image", title: "Cloud & Infrastructure", intro: "Our cloud-enabled mobile solutions are designed for scalability, performance, and security.", items: ["AWS", "Microsoft Azure", "Google Cloud Platform", "Firebase"] },
  { icon: database_icon, iconType: "image", title: "Databases", intro: "We implement secure and optimized data management solutions using:", items: ["MySQL", "PostgreSQL", "MongoDB", "Firebase Firestore"] },
  { icon: integration_icon, iconType: "image", title: "Third-Party Integrations", intro: "Our mobile app development services support seamless integration with:", items: ["Payment gateways", "CRM systems", "ERP platforms", "Analytics tools", "Marketing automation", "Authentication services"] },
];

const StackCard = ({ s, i, visible }) => (
  <div
    style={{ transitionDelay: `${i * 80}ms` }}
    className={`group relative bg-white border border-gray-100 hover:border-[#cd2122] rounded-3xl p-6 sm:p-7 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${
      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
    }`}
  >
    <div className="flex items-center gap-3.5 mb-4">
      <div className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 rounded-2xl bg-[#cd2122]/10 flex items-center justify-center group-hover:bg-[#cd2122] transition-all duration-300">
        {s.iconType === "image" ? (
          <img src={s.icon} alt={s.title} className="w-7 h-7 sm:w-8 sm:h-8 object-contain" />
        ) : (
          <span className="text-xl sm:text-2xl">{s.icon}</span>
        )}
      </div>
      <h4 className="font-bold text-[16px] sm:text-lg text-[#1a1a1a] leading-snug">{s.title}</h4>
    </div>

    {s.intro && <p className="text-[13.5px] sm:text-[14px] text-[#535353] mb-4 leading-relaxed">{s.intro}</p>}

    <div className="flex flex-wrap gap-2">
      {s.items.map((item, j) => (
        <span
          key={j}
          className="text-[12.5px] sm:text-[13px] font-medium text-[#535353] bg-[#fafafa] group-hover:bg-[#cd2122]/5 border border-gray-100 group-hover:border-[#cd2122]/20 px-3 py-1.5 rounded-full transition-colors duration-300"
        >
          {item}
        </span>
      ))}
    </div>
  </div>
);

const Section15 = () => {
  const [headRef, headVisible] = useReveal();
  const [gridRef, gridVisible] = useReveal();
  const [extraRef, extraVisible] = useReveal();
  const [footRef, footVisible] = useReveal();

  return (
    <section id="technologies" className="bg-white py-10 md:py-10 overflow-hidden">
      {/* Circuit banner strip */}
      <div className="relative h-[220px] sm:h-[250px] mb-14 sm:mb-16">
        <img src={techBanner} alt="Mobile app development technologies" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#cd2122] via-[#cd2122]/10 to-[#cd2122]/40" />
        <div
          ref={headRef}
          className={`absolute inset-0 flex flex-col items-center justify-center text-center px-6 transition-all duration-700 ${
            headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="text-[24px] sm:text-[28px] md:text-[38px] font-extrabold text-white mt-3 leading-tight drop-shadow-lg">
            Mobile App Development Technologies
          </h2>
          <p className="text-white text-[14px] sm:text-[16px] max-w-2xl mt-2">
            At Mean3, we use modern technologies and proven development frameworks to build secure, scalable, and high-performing mobile applications that support long-term business growth.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Mobile Technologies */}
        <div className="flex items-center gap-3 mb-7 sm:mb-8">
          <span className="w-8 h-[2px] bg-[#cd2122]" />
          <h3 className="text-xl sm:text-2xl font-bold text-[#1a1a1a]">Mobile Technologies</h3>
        </div>
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-14 sm:mb-16">
          {mobileStacks.map((s, i) => (
            <StackCard key={i} s={s} i={i} visible={gridVisible} />
          ))}
        </div>

        {/* Cloud, Databases, Integrations */}
        <div className="flex items-center gap-3 mb-7 sm:mb-8">
          <span className="w-8 h-[2px] bg-[#cd2122]" />
          <h3 className="text-xl sm:text-2xl font-bold text-[#1a1a1a]">Infrastructure & Integrations</h3>
        </div>
        <div ref={extraRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-12 sm:mb-14">
          {infraStacks.map((s, i) => (
            <StackCard key={i} s={s} i={i} visible={extraVisible} />
          ))}
        </div>

        {/* Closing statement */}
        <p
          ref={footRef}
          className={`text-center text-[#535353] text-[14.5px] sm:text-[16px] max-w-3xl mx-auto font-medium leading-relaxed transition-all duration-700 ${
            footVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          By selecting the right technology stack for each project, we ensure your mobile application remains flexible, scalable, secure, and ready for future growth.
        </p>
      </div>
    </section>
  );
};

export default Section15;